import { Amenity } from './amenity.model';
import { Booking } from './booking.model';
import moment from 'moment';

export type CalendarEventState = 'active' | 'cancelled' | 'past';

export type CalendarViewMode = 'day' | 'month' | 'week';

export interface CalendarColor {
  background: string;
  border: string;
  text: string;
}

export interface AmenityFilter {
  amenityId: string;
  color: CalendarColor;
  name: string;
  visible: boolean;
}

export interface CalendarEvent {
  amenityId: string;
  amenityName: string;
  ariaLabel: string;
  backgroundColor: string;
  booking: Booking;
  bookingId: string;
  borderColor: string;
  color: CalendarColor;
  createdAtLabel: string;
  createdByName: string;
  dayKey: string;
  durationMinutes: number;
  endAt: moment.Moment;
  endLabel: string;
  endMinutes: number;
  guests: number;
  hasEnded: boolean;
  heightPx: number;
  houseNumber: string;
  isBlocked: boolean;
  leftPercent: number;
  longDateLabel: string;
  ownerName: string;
  startAt: moment.Moment;
  startLabel: string;
  startMinutes: number;
  state: CalendarEventState;
  stateClass: string;
  stateLabel: string;
  streetName: string;
  textColor: string;
  timeRangeLabel: string;
  topPx: number;
  widthPercent: number;
}

export interface CalendarDay {
  dateKey: string;
  dayNumber: number;
  events: CalendarEvent[];
  isCurrentMonth: boolean;
  isToday: boolean;
  longLabel: string;
  moreCount: number;
  previewEvents: CalendarEvent[];
  weekdayLabel: string;
}

export interface CalendarWeek {
  days: CalendarDay[];
  weekKey: string;
}

export interface CalendarHourSlot {
  hour: number;
  label: string;
}

export interface CalendarSkeletonSlot {
  heightPx: number;
  key: string;
  topPx: number;
}

/**
 * Paleta fija tipo Google Calendar.
 * Cada entrada cumple contraste >= 4.5:1 entre `text` y `background`.
 * El indice se resuelve con un hash estable del amenityId,
 * por lo que una amenidad siempre conserva el mismo color.
 */
export const calendarPalette: CalendarColor[] = [
  { background: '#E8F0FE', border: '#1A73E8', text: '#174EA6' },
  { background: '#E6F4EA', border: '#188038', text: '#0D652D' },
  { background: '#F3E5F5', border: '#7B1FA2', text: '#4A148C' },
  { background: '#E0F2F1', border: '#00796B', text: '#004D40' },
  { background: '#E8EAF6', border: '#3F51B5', text: '#283593' },
  { background: '#FEF7E0', border: '#B06000', text: '#7A4100' },
  { background: '#FCE4EC', border: '#C2185B', text: '#880E4F' },
  { background: '#E1F5FE', border: '#0277BD', text: '#01579B' },
  { background: '#F1F8E9', border: '#558B2F', text: '#33691E' },
  { background: '#ECEFF1', border: '#455A64', text: '#263238' }
];

export const calendarCancelledColor: CalendarColor = {
  background: '#F1F3F4',
  border: '#80868B',
  text: '#3C4043'
};

export interface CreateAmenityOption {
  amenity: Amenity;
  color: CalendarColor;
}

export interface StateFilter {
  label: string;
  state: CalendarEventState;
  stateClass: string;
  visible: boolean;
}

export function amenityColorOf(amenityId: string): CalendarColor {

  let hash = 0;

  for (let index = 0; index < amenityId.length; index++) {
    hash = (hash * 31 + amenityId.charCodeAt(index)) % 1000003;
  }

  return calendarPalette[hash % calendarPalette.length];
}
